import { useEffect, useState } from "react";

import { ReactComponent as AlertTriangle } from "assets/images/iconsV2/alert-triangle-danger.svg";
import Button from "components/v2/Button/Button";
import Modal from "components/v2/Modal/Modal";
import IconCircle from "components/v2/IconCircle/IconCircle";

interface UseUnsavedChangesWarningOptions {
  isDirty: boolean;
  title?: string;
  message?: string;
  confirmBtnText?: string;
  cancelBtnText?: string;
}

const useUnsavedChangesWarning = ({
  isDirty,
  title = "Discard changes?",
  message = "All of your entered information will be lost. Proceed?",
  confirmBtnText = "Discard",
  cancelBtnText = "Cancel",
}: UseUnsavedChangesWarningOptions) => {
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Handle browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const handleConfirm = () => {
    setShowModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setPendingNavigation(null);
  };

  // Method to programmatically check before navigation (for custom buttons like Discard)
  const confirmNavigation = (onConfirm: () => void) => {
    if (isDirty) {
      setPendingNavigation(() => onConfirm);
      setShowModal(true);
    } else {
      onConfirm();
    }
  };

  function UnsavedChangesModal() {
    return (
      <Modal show={showModal} onHide={handleCancel} size="md">
        <Modal.Header>
          <div className="d-flex flex-column">
            <div className="align-self-start">
              <IconCircle icon={AlertTriangle} size="lg" color="error" />
            </div>
            <div className="mt-xl text-primary text-xl font-semibold">{title}</div>
            <div className="text-tertiary text-sm">{message}</div>
          </div>
        </Modal.Header>
        <Modal.Footer>
          <div className="d-flex align-items-center justify-content-end">
            <div className="mr-lg">
              <Button label={cancelBtnText} onClick={handleCancel} variant="secondary-gray" />
            </div>
            <Button label={confirmBtnText} onClick={handleConfirm} variant="destructive-primary" />
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  return { UnsavedChangesModal, confirmNavigation };
};

export default useUnsavedChangesWarning;
